/**
 * Format a date object or string to a localized readable format
 * @param date Date to format (Date object or string)
 * @returns Formatted date string
 */
export const formatDate = (date: Date | string): string => {
  // Ensure we have a Date object
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Format the date
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format a date with time
 * @param date - The date to format
 * @returns Formatted date and time string
 */
export const formatDateTime = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

/**
 * Get relative time string (e.g., "2 days ago")
 * @param dateString - ISO date string
 * @returns Relative time string
 */
export const getRelativeTimeString = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInSecs = Math.floor(diffInMs / 1000);
  const diffInMins = Math.floor(diffInSecs / 60);
  const diffInHours = Math.floor(diffInMins / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInSecs < 60) return diffInSecs === 1 ? '1 second ago' : `${diffInSecs} seconds ago`;
  if (diffInMins < 60) return diffInMins === 1 ? '1 minute ago' : `${diffInMins} minutes ago`;
  if (diffInHours < 24) return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
  if (diffInDays < 7) return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
  if (diffInWeeks < 4) return diffInWeeks === 1 ? '1 week ago' : `${diffInWeeks} weeks ago`;
  if (diffInMonths < 12) return diffInMonths === 1 ? '1 month ago' : `${diffInMonths} months ago`;
  return diffInYears === 1 ? '1 year ago' : `${diffInYears} years ago`;
};

/**
 * Calculate reading time in minutes based on content length
 * @param content Text content
 * @param wordsPerMinute Average reading speed (default: 200 words per minute)
 * @returns Estimated reading time in minutes
 */
export const calculateReadingTime = (content: string, wordsPerMinute = 200): number => {
  const wordCount = content.split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
}; 